import { forwardRef } from "react";
import { formatAuMobile } from "@/lib/auPhone";

interface AuMobileInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  hasError?: boolean;
  /** Tailwind classes for the inner input, matching the form's other fields. */
  inputClassName: string;
  ariaLabel?: string;
  placeholder?: string;
}

/**
 * Australian mobile input: fixed, non-editable +61 prefix with live formatting.
 * `value` is stored as the national portion (e.g. "412 345 678").
 */
const AuMobileInput = forwardRef<HTMLInputElement, AuMobileInputProps>(
  ({ id, value, onChange, onBlur, hasError, inputClassName, ariaLabel, placeholder = "412 345 678" }, ref) => (
    <div
      className={`flex items-stretch overflow-hidden rounded-xl border-2 bg-white transition-colors focus-within:border-[#FFC300] ${
        hasError ? "border-red-400" : "border-[#1a1a1a]/10"
      }`}
    >
      <span
        aria-hidden="true"
        className="flex items-center gap-2 pl-4 pr-3 font-bold text-[#1a1a1a]/70 select-none border-r border-[#1a1a1a]/10 bg-[#1a1a1a]/[0.03]"
      >
        +61
      </span>
      <input
        ref={ref}
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        aria-label={ariaLabel ?? "Australian mobile number"}
        aria-invalid={hasError || undefined}
        placeholder={placeholder}
        value={value}
        onBlur={onBlur}
        onChange={(e) => onChange(formatAuMobile(e.target.value))}
        className={`${inputClassName} border-0 rounded-none focus:border-0 focus:ring-0 flex-1 min-w-0`}
      />
    </div>
  ),
);
AuMobileInput.displayName = "AuMobileInput";

export default AuMobileInput;
